import { cross3, dot3, length3, normalize3, sub3, type Vec3 } from './vector';
import { normalizeQuaternion, type Quat } from './quaternion';

/** Column-major matrices, multiplied by column vectors: clip = projection * view * world * point. */
export type Mat4 = Float32Array;

export function identity4(): Mat4 {
  return new Float32Array([1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]);
}

/** Returns a * b; neither input is modified. */
export function multiply4(a: Mat4, b: Mat4): Mat4 {
  const result = new Float32Array(16);
  for (let column = 0; column < 4; column++) {
    for (let row = 0; row < 4; row++) {
      result[column * 4 + row] =
        a[row]! * b[column * 4]! +
        a[4 + row]! * b[column * 4 + 1]! +
        a[8 + row]! * b[column * 4 + 2]! +
        a[12 + row]! * b[column * 4 + 3]!;
    }
  }
  return result;
}

/** Throws for a singular matrix instead of silently emitting NaN transforms. */
export function invert4(matrix: Mat4): Mat4 {
  // Gauss-Jordan elimination with partial pivoting, using double precision while solving.
  const rows = Array.from({ length: 4 }, (_, row) => [
    matrix[row]!, matrix[row + 4]!, matrix[row + 8]!, matrix[row + 12]!,
    row === 0 ? 1 : 0, row === 1 ? 1 : 0, row === 2 ? 1 : 0, row === 3 ? 1 : 0,
  ]);
  for (let column = 0; column < 4; column++) {
    let pivot = column;
    for (let row = column + 1; row < 4; row++) {
      if (Math.abs(rows[row]![column]!) > Math.abs(rows[pivot]![column]!)) pivot = row;
    }
    const magnitude = Math.abs(rows[pivot]![column]!);
    if (!Number.isFinite(magnitude) || magnitude === 0) throw new RangeError('Cannot invert a singular matrix');
    [rows[column], rows[pivot]] = [rows[pivot]!, rows[column]!];
    const pivotRow = rows[column]!;
    const divisor = pivotRow[column]!;
    for (let k = 0; k < 8; k++) pivotRow[k] = pivotRow[k]! / divisor;
    for (let row = 0; row < 4; row++) {
      if (row === column) continue;
      const target = rows[row]!;
      const factor = target[column]!;
      for (let k = 0; k < 8; k++) target[k] = target[k]! - factor * pivotRow[k]!;
    }
  }
  const result = new Float32Array(16);
  for (let row = 0; row < 4; row++) {
    for (let column = 0; column < 4; column++) result[column * 4 + row] = rows[row]![column + 4]!;
  }
  return result;
}

/** Right-handed projection looking down -Z, with WebGPU depth 0..1. FOV is vertical radians. */
export function perspective4(fovY: number, aspect: number, near: number, far: number): Mat4 {
  if (!(fovY > 0 && fovY < Math.PI && aspect > 0 && near > 0 && far > near)) {
    throw new RangeError('Perspective requires 0 < fovY < PI, aspect > 0, and 0 < near < far');
  }
  const f = 1 / Math.tan(fovY / 2);
  const z = far === Infinity ? -1 : far / (near - far);
  const translationZ = far === Infinity ? -near : near * far / (near - far);
  return new Float32Array([f / aspect, 0, 0, 0, 0, f, 0, 0, 0, 0, z, -1, 0, 0, translationZ, 0]);
}

/** Right-handed orthographic projection; near/far are positive distances along view -Z. */
export function orthographic4(left: number, right: number, bottom: number, top: number, near: number, far: number): Mat4 {
  if (!(right > left && top > bottom && far > near)) throw new RangeError('Orthographic bounds must have positive extent');
  return new Float32Array([
    2 / (right - left), 0, 0, 0,
    0, 2 / (top - bottom), 0, 0,
    0, 0, 1 / (near - far), 0,
    -(right + left) / (right - left), -(top + bottom) / (top - bottom), near / (near - far), 1,
  ]);
}

/** Returns a view matrix. A collinear up vector gets a stable alternate up axis. */
export function lookAt4(eye: Vec3, target: Vec3, up: Vec3 = [0, 1, 0]): Mat4 {
  let back = normalize3(sub3(eye, target));
  if (length3(back) === 0) back = [0, 0, 1];
  let right = cross3(up, back);
  if (length3(right) < 1e-8) right = cross3(Math.abs(back[1]) > 0.99 ? [0, 0, 1] : [0, 1, 0], back);
  right = normalize3(right);
  const actualUp = cross3(back, right);
  return new Float32Array([
    right[0], actualUp[0], back[0], 0,
    right[1], actualUp[1], back[1], 0,
    right[2], actualUp[2], back[2], 0,
    -dot3(right, eye), -dot3(actualUp, eye), -dot3(back, eye), 1,
  ]);
}

/** Builds translation * Y rotation * scale. Rotation is in radians. */
export function compose4(position: Vec3, rotationY: number, scale: Vec3): Mat4 {
  const c = Math.cos(rotationY);
  const s = Math.sin(rotationY);
  return new Float32Array([
    c * scale[0], 0, -s * scale[0], 0,
    0, scale[1], 0, 0,
    s * scale[2], 0, c * scale[2], 0,
    position[0], position[1], position[2], 1,
  ]);
}

/** Builds translation * quaternion rotation * scale using the same axes as compose4. */
export function composeQuaternion4(position: Vec3, orientation: Quat, scale: Vec3): Mat4 {
  const [x, y, z, w] = normalizeQuaternion(orientation);
  const xx = x * x, yy = y * y, zz = z * z;
  const xy = x * y, xz = x * z, yz = y * z;
  const wx = w * x, wy = w * y, wz = w * z;
  return new Float32Array([
    (1 - 2 * (yy + zz)) * scale[0], 2 * (xy + wz) * scale[0], 2 * (xz - wy) * scale[0], 0,
    2 * (xy - wz) * scale[1], (1 - 2 * (xx + zz)) * scale[1], 2 * (yz + wx) * scale[1], 0,
    2 * (xz + wy) * scale[2], 2 * (yz - wx) * scale[2], (1 - 2 * (xx + yy)) * scale[2], 0,
    position[0], position[1], position[2], 1,
  ]);
}

export function transformPoint3(point: Vec3, matrix: Mat4): Vec3 {
  const [x, y, z] = point;
  const w = matrix[3]! * x + matrix[7]! * y + matrix[11]! * z + matrix[15]!;
  if (Math.abs(w) < Number.EPSILON) throw new RangeError('Point transforms to infinity');
  return [
    (matrix[0]! * x + matrix[4]! * y + matrix[8]! * z + matrix[12]!) / w,
    (matrix[1]! * x + matrix[5]! * y + matrix[9]! * z + matrix[13]!) / w,
    (matrix[2]! * x + matrix[6]! * y + matrix[10]! * z + matrix[14]!) / w,
  ];
}

/** Transforms a vector with w=0. Does not normalize, so ray parameters remain consistent. */
export function transformDirection3(direction: Vec3, matrix: Mat4): Vec3 {
  const [x, y, z] = direction;
  return [
    matrix[0]! * x + matrix[4]! * y + matrix[8]! * z,
    matrix[1]! * x + matrix[5]! * y + matrix[9]! * z,
    matrix[2]! * x + matrix[6]! * y + matrix[10]! * z,
  ];
}

export interface ProjectedPoint {
  /** CSS pixels from the left/top of the viewport. */
  readonly x: number;
  readonly y: number;
  /** WebGPU normalized depth; visible points have depth in 0..1. */
  readonly depth: number;
  readonly visible: boolean;
}

/** Projects a world point into a top-left-origin viewport. */
export function projectPoint3(point: Vec3, viewProjection: Mat4, width: number, height: number): ProjectedPoint {
  const [x, y, z] = point;
  const m = viewProjection;
  const w = m[3]! * x + m[7]! * y + m[11]! * z + m[15]!;
  if (w <= 0) return { x: 0, y: 0, depth: 0, visible: false };
  const ndcX = (m[0]! * x + m[4]! * y + m[8]! * z + m[12]!) / w;
  const ndcY = (m[1]! * x + m[5]! * y + m[9]! * z + m[13]!) / w;
  const depth = (m[2]! * x + m[6]! * y + m[10]! * z + m[14]!) / w;
  return {
    x: (ndcX + 1) * width / 2,
    y: (1 - ndcY) * height / 2,
    depth,
    visible: ndcX >= -1 && ndcX <= 1 && ndcY >= -1 && ndcY <= 1 && depth >= 0 && depth <= 1,
  };
}
